import { HelperMethods } from '_utils';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import React, { useRef } from 'react';
import Barcode from 'react-barcode';
import {
  MdCheckCircle,
  MdDownload,
  MdEvent,
  MdLocationOn,
  MdPeople,
  MdPhotoCamera,
  MdPrint,
} from 'react-icons/md';
import main_logo from '../../assets/logos/main_logo.png';

import { SkatingLessonData } from '@types';
import { excludeFromTicketCapture, SuccessAppDownloadBanner } from '_atoms';

interface SkatingLessonConfirmationProps {
  bookingData: SkatingLessonData;
  ticketNumber: string;
}

export const SkatingLessonConfirmation: React.FC<
  SkatingLessonConfirmationProps
> = ({ bookingData, ticketNumber }) => {
  const ticketElementRef = useRef<HTMLDivElement>(null);

  const generateTicketPDF = async (): Promise<{
    pdf: jsPDF;
    filename: string;
  }> => {
    if (!ticketElementRef.current) {
      throw new Error('Ticket element not found');
    }

    // Convert the ticket element to an image
    const png = await toPng(ticketElementRef.current, {
      quality: 1.0,
      backgroundColor: '#ffffff',
      filter: excludeFromTicketCapture,
    });

    // Create a new jsPDF instance
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Create an image element to get dimensions
    const img = new Image();
    img.src = png;

    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          // Calculate dimensions to fit the image on the page
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = (img.height * imgWidth) / img.width;

          // If image is too tall, scale it down
          let finalWidth = imgWidth;
          let finalHeight = imgHeight;

          if (imgHeight > pageHeight - margin * 2) {
            finalHeight = pageHeight - margin * 2;
            finalWidth = (img.width * finalHeight) / img.height;
          }

          // Center the image on the page
          const x = (pageWidth - finalWidth) / 2;
          const y = (pageHeight - finalHeight) / 2;

          // Add the image to the PDF
          pdf.addImage(png, 'PNG', x, y, finalWidth, finalHeight);

          // Generate filename using lesson date
          const filename = `MOI_SkatingLesson_${bookingData.selectedLesson?.title.replace(/ /g, '_')}.pdf`;

          resolve({ pdf, filename });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load ticket image'));
      };
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const { pdf, filename } = await generateTicketPDF();
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handlePrint = async () => {
    try {
      const { pdf } = await generateTicketPDF();
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.print();
      }
    } catch (error) {
      console.error('Error printing:', error);
    }
  };

  const handleSavePhoto = async () => {
    try {
      if (!ticketElementRef.current) {
        throw new Error('Ticket element not found');
      }

      // Convert the ticket element to a PNG image
      const dataUrl = await toPng(ticketElementRef.current, {
        quality: 1.0,
        backgroundColor: '#ffffff',
        pixelRatio: 2, // Higher resolution for better quality
        filter: excludeFromTicketCapture,
      });

      // Create a download link
      const lessonTitle = bookingData.selectedLesson?.title || 'SkatingLesson';
      const link = document.createElement('a');
      link.download = `MOI_SkatingLesson_${lessonTitle.replace(/ /g, '_')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error saving photo:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Ticket Content */}
      <div ref={ticketElementRef} className="space-y-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={main_logo} alt="MOI Logo" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold">
                  Skating Lesson Confirmation
                </h1>
                <p className="text-blue-100">
                  Your skating lesson has been successfully booked!
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <MdCheckCircle className="text-green-400 w-6 h-6" />
                <span className="font-semibold">Confirmed</span>
              </div>
              <p className="text-sm text-blue-100">
                {HelperMethods.formatDateTimeLocal(new Date().toISOString())}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Lesson Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <MdEvent className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Lesson Information</p>
                  <p className="font-medium text-gray-900">
                    {bookingData.selectedLesson?.title}
                  </p>
                  <p
                    className="font-medium text-gray-900"
                    dangerouslySetInnerHTML={{
                      __html: bookingData.selectedLesson?.description || '',
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MdPeople className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">
                    Number of Participants
                  </p>
                  <p className="font-medium text-gray-900">
                    {bookingData.participantsInfo?.length || 0} participants
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MdLocationOn className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <a
                    href="https://www.google.com/maps/dir/Modesto+on+Ice,+11th+St,+Modesto,+CA+95354"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    1131 11th Street • Modesto, CA 95354
                  </a>
                </div>
              </div>
            </div>

            {/* Barcode and QR Code */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Barcode
                </p>
                <div className="bg-white border border-gray-300 p-3 rounded">
                  <div className="font-mono text-xs leading-tight text-center flex items-center justify-center">
                    <Barcode value={ticketNumber} format="CODE128" />
                  </div>
                  <p className="text-xs text-center text-gray-600 mt-1">
                    {ticketNumber}
                  </p>
                </div>
              </div>

              {/* <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  QR Code
                </p>
                <div className="bg-white border border-gray-300 p-3 rounded">
                  <div className="font-mono text-xs leading-tight text-center">
                    <QRCode value={ticketNumber} />
                  </div>
                </div>
              </div> */}
            </div>
          </div>

          {/* Participant Details */}
          {bookingData.participantsInfo &&
            bookingData.participantsInfo.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  Participant Details
                </h4>
                <div className="space-y-2">
                  {bookingData.participantsInfo.map(
                    (participant: any, index: number) => {
                      const waiver = bookingData.skaterWaivers?.find(
                        (w: any) =>
                          w.skaterName ===
                          `${participant.firstName} ${participant.lastName}`,
                      );
                      return (
                        <div
                          key={participant.id}
                          className="flex justify-between items-center bg-gray-50 p-3 rounded"
                        >
                          <div>
                            <p className="font-medium text-gray-900">
                              {participant.firstName} {participant.lastName}
                            </p>
                            <p className="text-sm text-gray-600">
                              DOB:{' '}
                              {HelperMethods.formatDate(
                                participant.dateOfBirth,
                              )}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {waiver || bookingData.isSkatingWaiverAttached ? (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                <MdCheckCircle className="w-3 h-3 mr-1" />
                                Waiver Signed
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Waiver Pending
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}

          {/* Customer Information */}
          {bookingData.customerInfo && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Customer Information
              </h4>
              <div className="bg-gray-50 p-4 rounded">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">
                      {bookingData.customerInfo.firstName}{' '}
                      {bookingData.customerInfo.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">
                      {bookingData.customerInfo.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">
                      {bookingData.customerInfo.phoneNumber}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Total Amount */}
          {bookingData.selectedLesson?.price && (
            <div className="border-t border-gray-200 pt-4">
              <div className="bg-blue-50 p-4 rounded space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">
                    Subtotal ({bookingData.participantsInfo?.length || 1}{' '}
                    participant
                    {(bookingData.participantsInfo?.length || 1) > 1 ? 's' : ''}
                    )
                  </span>
                  <span className="text-gray-900">
                    $
                    {(
                      bookingData.selectedLesson.price *
                      (bookingData.participantsInfo?.length || 1)
                    ).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Convenience Fee (3%)</span>
                  <span className="text-gray-900">
                    $
                    {(
                      bookingData.selectedLesson.price *
                      (bookingData.participantsInfo?.length || 1) *
                      0.03
                    ).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Amount:
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    $
                    {(
                      bookingData.selectedLesson.price *
                      (bookingData.participantsInfo?.length || 1) *
                      1.03
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <SuccessAppDownloadBanner />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={handlePrint}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdPrint className="w-4 h-4 mr-2" />
            Print
          </button>
          <button
            onClick={handleSavePhoto}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <MdPhotoCamera className="w-4 h-4 mr-2" />
            Save Photo
          </button>
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdDownload className="w-4 h-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};
